import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Briefcase, Instagram, Twitter, Linkedin, Globe, Play, Radio, Youtube } from 'lucide-react';
import axios from 'axios';
import { ListenModal } from '../components/ListenModal';
import { API_URL } from '../utils/apiUrl';
import { formatSeries } from '../utils/formatSeries';
import { extractYouTubeId } from '../utils/extractYouTubeId';
import { SEOHead } from '../components/SEOHead';
import { JoinClubhouseCTA } from '../components/JoinClubhouseCTA';
import './GuestDetail.css';

export const GuestDetail = () => {
  const { id } = useParams();
  const [guest, setGuest] = useState(null);
  const [guestEpisodes, setGuestEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEpisode, setSelectedEpisode] = useState(null);

  useEffect(() => {
    fetchGuestData();
  }, [id]);

  const fetchGuestData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch guest details and all episodes
      const [guestRes, episodesRes] = await Promise.all([
        axios.get(`${API_URL}/api/cms/guests/${id}`),
        axios.get(`${API_URL}/api/cms/episodes`)
      ]);
      
      setGuest(guestRes.data);
      
      // Find all episodes where this guest is tagged
      const taggedEpisodes = episodesRes.data.filter(
        ep => ep.guests && ep.guests.includes(id)
      );
      setGuestEpisodes(taggedEpisodes);
      
    } catch (err) {
      console.error('Error fetching guest data:', err);
      setError(err.response?.status === 404 ? 'Guest not found' : 'Error loading guest');
    } finally {
      setLoading(false);
    }
  };

  const handleListenClick = (episode) => {
    setSelectedEpisode(episode);
    setIsModalOpen(true);
  };

  if (error || !guest) {
    return (
      <div className="guest-not-found">
        <div className="container">
          <h1 className="heading-1">{error || 'Guest Not Found'}</h1>
          <Link to="/guests" className="btn-primary">Back to Guests</Link>
        </div>
      </div>
    );
  }

  // Build short, plain-text bio for SEO meta
  const guestBio = (() => {
    if (!guest.bio) return `${guest.name} — ${guest.role || 'guest'} on the Golf Nuts podcast.`;
    const tmp = document.createElement('div');
    tmp.innerHTML = guest.bio;
    return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim().slice(0, 200);
  })();

  return (
    <div className="guest-detail-page">
      <SEOHead
        title={guest.name}
        description={guestBio}
        path={`/guests/${guest.id}`}
        ogType="profile"
        image={guest.image}
      />
      <div className="container">
        <Link to="/guests" className="back-link">
          <ArrowLeft size={20} />
          <span>Back to Guests</span>
        </Link>

        <div className="guest-detail-header">
          <div className="guest-detail-image">
            <img src={guest.image} alt={guest.name} />
          </div>
          
          <div className="guest-detail-info">
            <h1 className="display-medium">{guest.name}</h1>
            <div className="guest-role-info">
              <Briefcase size={24} />
              <div>
                <p className="guest-role-large">{guest.role}</p>
                <p className="guest-company-large">{guest.company}</p>
              </div>
            </div>
            <p className="body-large guest-bio-full">{guest.bio}</p>
            
            {/* Social Links */}
            {guest.socialLinks && Object.keys(guest.socialLinks).some(key => guest.socialLinks[key]) && (
              <div className="guest-social-links">
                {guest.socialLinks.instagram && (
                  <a href={guest.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="guest-social-link instagram">
                    <Instagram size={20} />
                  </a>
                )}
                {guest.socialLinks.twitter && (
                  <a href={guest.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="guest-social-link twitter">
                    <Twitter size={20} />
                  </a>
                )}
                {guest.socialLinks.youtube && (
                  <a href={guest.socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="guest-social-link youtube">
                    <Youtube size={20} />
                  </a>
                )}
                {guest.socialLinks.tiktok && (
                  <a href={guest.socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className="guest-social-link tiktok">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </a>
                )}
                {guest.socialLinks.linkedin && (
                  <a href={guest.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="guest-social-link linkedin">
                    <Linkedin size={20} />
                  </a>
                )}
                {guest.socialLinks.website && (
                  <a href={guest.socialLinks.website} target="_blank" rel="noopener noreferrer" className="guest-social-link website">
                    <Globe size={20} />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Episodes Section */}
        <div className="guest-episodes-section">
          <h2 className="heading-1">
            Episodes Featuring {guest.name}
            <span className="episodes-count-badge">{guestEpisodes.length}</span>
          </h2>
          
          {guestEpisodes.length === 0 ? (
            <div className="no-episodes-message">
              <p className="body-large">No episodes featuring this guest yet. Check back soon!</p>
            </div>
          ) : (
            <div className="guest-episodes-list">
              {guestEpisodes.map((episode) => {
                const youtubeId = extractYouTubeId(episode.youtubeId);
                const hasValidYouTube = youtubeId && /^[a-zA-Z0-9_-]{11}$/.test(youtubeId);
                const thumbnailUrl = hasValidYouTube 
                  ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`
                  : episode.thumbnail;
                
                return (
                  <div key={episode.id} className="guest-episode-card">
                    <Link to={`/episodes/${episode.slug}`} className="episode-card-link">
                      <div className="episode-card-thumbnail">
                        <img 
                          src={thumbnailUrl} 
                          alt={episode.title}
                          onError={(e) => {
                            if (episode.thumbnail) {
                              e.target.src = episode.thumbnail;
                            }
                          }}
                        />
                        {hasValidYouTube && (
                          <div className="episode-play-overlay">
                            <Play size={32} color="white" />
                          </div>
                        )}
                        {!hasValidYouTube && (
                          <div className="episode-coming-soon-overlay">
                            <span>Coming Soon</span>
                          </div>
                        )}
                      </div>
                      <div className="episode-card-details">
                        <span className="episode-series-label">{formatSeries(episode.series)}</span>
                        <h3 className="heading-3">{episode.title}</h3>
                        <p className="body-small episode-meta-text">
                          {hasValidYouTube ? episode.duration : 'Coming Soon'} • {new Date(episode.publishDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                        <p className="body-medium episode-description-text">{episode.description}</p>
                      </div>
                    </Link>
                    <div className="episode-actions">
                      <Link 
                        to={`/episodes/${episode.slug}`}
                        className="episode-watch-btn"
                      >
                        <Play size={18} />
                        <span>Watch</span>
                      </Link>
                      <button 
                        onClick={() => handleListenClick(episode)}
                        className="episode-listen-btn"
                      >
                        <Radio size={18} />
                        <span>Listen</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Membership CTA — converts SEO traffic on guest pages */}
          <JoinClubhouseCTA variant="guest" />
        </div>
      </div>

      {/* Listen Modal */}
      <ListenModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        episode={selectedEpisode}
      />
    </div>
  );
};
